// Popup con las notas que publicó un medio sobre la crisis.
//
// Se abre al tocar un medio en "Quién lo publicó". Sirve para dos cosas
// concretas antes de levantar el teléfono: ver con qué tono viene cubriendo y
// cuándo fue la última vez que dijo algo.

import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FaTimes, FaExternalLinkAlt, FaNewspaper } from 'react-icons/fa';
import { mono, borderSoft } from '../../styles/techComponents';

const Fondo = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(11, 19, 32, 0.85);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const Caja = styled.div`
  background: var(--panel-bg, #0f172a);
  border: 1px solid ${borderSoft};
  border-radius: 12px;
  max-width: 720px;
  width: 100%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
`;

const Cabecera = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 1rem 1.3rem;
  border-bottom: 1px solid ${borderSoft};
`;

const Titulo = styled.h3`
  margin: 0 0 3px;
  color: var(--text-primary);
  font-size: 1.05rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Bajada = styled.div`
  font-family: ${mono};
  font-size: 0.7rem;
  color: var(--text-secondary);
  letter-spacing: 0.04em;
`;

const Cerrar = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 1rem;
  padding: 4px;
  display: flex;
  flex-shrink: 0;
  border-radius: 4px;
  &:hover { color: var(--text-primary); background: rgba(255, 255, 255, 0.06); }
`;

const Cuerpo = styled.div`
  overflow-y: auto;
  padding: 0.4rem 1.3rem 1.1rem;
`;

const Nota = styled.a`
  display: block;
  padding: 12px 0;
  border-bottom: 1px solid ${borderSoft};
  text-decoration: none;
  color: inherit;
  &:last-child { border-bottom: none; }
  &:hover .t { color: var(--btn-primary); }
`;

const TituloNota = styled.div`
  font-size: 0.9rem;
  line-height: 1.5;
  color: var(--text-primary);
`;

const MetaNota = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Vacio = styled.p`
  margin: 1rem 0;
  color: var(--text-secondary);
  font-size: 0.86rem;
  line-height: 1.55;
`;

export default function NotasMedioModal({ medio, notas, total, onCerrar }) {
  const cajaRef = useRef(null);

  // Cerrar con Escape y no dejar que el fondo scrollee mientras está abierto.
  useEffect(() => {
    const alTecla = (e) => { if (e.key === 'Escape') onCerrar(); };
    document.addEventListener('keydown', alTecla);
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    cajaRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', alTecla);
      document.body.style.overflow = overflowPrevio;
    };
  }, [onCerrar]);

  if (!medio) return null;

  const ultima = notas.length
    ? new Date(notas[0].fecha).toLocaleDateString('es-AR')
    : null;

  return (
    <Fondo
      onClick={onCerrar}
      role="dialog"
      aria-modal="true"
      aria-label={`Notas de ${medio}`}
    >
      {/* El click adentro no cierra: solo el del fondo. */}
      <Caja ref={cajaRef} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
        <Cabecera>
          <div>
            <Titulo><FaNewspaper /> {medio}</Titulo>
            <Bajada>
              {/* Si el contador del medio dice más de las que se pueden listar,
                  se aclara en vez de dejar una diferencia sin explicación. */}
              {total && total > notas.length
                ? `${notas.length} DE ${total} NOTAS`
                : `${notas.length} ${notas.length === 1 ? 'NOTA' : 'NOTAS'}`}
              {ultima ? ` · ÚLTIMA EL ${ultima}` : ''}
            </Bajada>
          </div>
          <Cerrar onClick={onCerrar} aria-label="Cerrar"><FaTimes /></Cerrar>
        </Cabecera>

        <Cuerpo>
          {notas.length === 0 ? (
            <Vacio>
              Este medio publicó sobre el tema, pero las notas quedaron fuera de las últimas
              que trae la plataforma.
            </Vacio>
          ) : (
            notas.map((n, i) => (
              <Nota
                key={n.url || i}
                href={n.url || undefined}
                target={n.url ? '_blank' : undefined}
                rel="noopener noreferrer"
                onClick={(e) => { if (!n.url) e.preventDefault(); }}
              >
                <TituloNota className="t">{n.title}</TituloNota>
                <MetaNota>
                  {new Date(n.fecha).toLocaleDateString('es-AR')}
                  {n.url && <>· abrir la nota <FaExternalLinkAlt style={{ fontSize: '0.6rem' }} /></>}
                </MetaNota>
              </Nota>
            ))
          )}
        </Cuerpo>
      </Caja>
    </Fondo>
  );
}
